import React, { useMemo, useEffect } from "react";
import { z } from "zod";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ComboboxDemo } from "@/components/ui/combobox";
import {
  PrivateOfficeStatus,
  PrivateOfficeLifecycleState,
  LIFECYCLE_STATE_LABELS,
  isTransientLifecycleState,
  MANUAL_LIFECYCLE_STATE_OPTIONS,
  NUMERIC_TO_STRING_STATUS_MAP,
  MANUAL_LIFECYCLE_STATE_OPTIONS_INTERNAL_USER,
} from "@/@types/private-office.types";
import { useSpaceSelection } from "@/hooks/useSpaceSelection";
import { spaceService } from "@/services/space-service";
import { debounce } from "@/utils/debounce";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StepProps } from "@/components/multi-step-form";
import { generatePrivateRoomUrl } from "@/features/private-room/list/utils/generate-private-room-url/generate-private-room-url";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useToast } from "@/components/ui/use-toast";
import { Switch } from "@/components/ui/switch";
import { useStatisticalAnalysis } from "@/hooks/useStatisticalAnalysis";
import { useAuthGuard } from "@/hooks/use-auth-guard";
import {
  NeighborhoodMissingAlert,
  PriceAnalysisAlert,
  SizeCapacityAlert,
} from "@/components/statistical-alerts";

export const dadosPrincipaisEditSchema = z.object({
  externalSpaceId: z.string().optional(), // Optional for edit since space is pre-selected
  spaceName: z.string().optional(),
  spaceRooms: z.array(z.any()).optional(),
  neighborhood: z.string().nullable().optional(),
  name: z
    .string({ required_error: 'O campo "Nome" é obrigatório' })
    .min(1, 'O campo "Nome" é obrigatório')
    .max(50, "O nome da sala deve ter no máximo 50 caracteres"),
  size: z
    .string({ required_error: "O tamanho deve ser maior que 5 m²" })
    .refine((val) => {
      if (!val || val.trim() === "") return false;
      const num = parseFloat(val);
      return !isNaN(num) && num > 5;
    }, "O tamanho deve ser maior que 5 m²"),
  capacity: z
    .string({ required_error: "A capacidade deve ser no mínimo 1 pessoa" })
    .refine((val) => {
      if (!val || val.trim() === "") return false;
      const num = parseInt(val);
      return !isNaN(num) && num >= 1;
    }, "A capacidade deve ser no mínimo 1 pessoa"),
  status: z
    .string({ required_error: 'O campo "Status" é obrigatório' })
    .min(1, 'O campo "Status" é obrigatório'),
  monthlyPrice: z
    .string({ required_error: "O preço mensal deve ser maior que R$ 500" })
    .refine((val) => {
      if (!val || val.trim() === "") return false;
      const cleanedValue = val.replace(/\./g, "").replace(",", ".");
      const num = parseFloat(cleanedValue);
      return !isNaN(num) && num >= 500;
    }, "O preço mensal deve ser igual ou maior que R$ 500"),
  description: z.string().optional(),
  notes: z.string().optional(),
  daysOfOperation: z
    .enum(["monday-friday", "monday-saturday", "custom"])
    .optional(),
  operatingHours: z
    .object({
      sunday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      monday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      tuesday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      wednesday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      thursday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      friday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      saturday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
    })
    .optional(),
  timezone: z.string().optional(),
  lifecycleState: z.nativeEnum(PrivateOfficeLifecycleState).optional(),
  availabilityDate: z.string().optional(),
});

export const dadosPrincipaisEditSchemaWithRefinement = dadosPrincipaisEditSchema
  .refine(
    (data) => {
      if (data.status === "unavailable") {
        return !!data.lifecycleState;
      }
      return true;
    },
    {
      message:
        "Para salvar, é necessário informar o motivo de indisponibilidade e a data prevista de retorno.",
      path: ["lifecycleState"],
    },
  )
  .refine(
    (data) => {
      if (
        data.status === "unavailable" &&
        data.lifecycleState &&
        isTransientLifecycleState(
          data.lifecycleState as PrivateOfficeLifecycleState,
        )
      ) {
        return !!data.availabilityDate;
      }
      return true;
    },
    {
      message:
        "A data prevista de retorno é obrigatória para o motivo selecionado.",
      path: ["availabilityDate"],
    },
  )
  .refine(
    (data) => {
      if (!data.availabilityDate) return true;
      const today = new Date().toISOString().split("T")[0];
      return data.availabilityDate >= today;
    },
    {
      message: "A data de retorno não pode ser anterior à data atual",
      path: ["availabilityDate"],
    },
  );

export type DadosPrincipaisEditData = z.infer<typeof dadosPrincipaisEditSchema>;

const todayIso = new Date().toISOString().split("T")[0];

interface StepDadosPrincipaisEditProps extends StepProps {
  onStatisticalDataReady?: (data: any) => void;
}

export const StepDadosPrincipaisEdit: React.FC<
  StepDadosPrincipaisEditProps
> = ({ errors, registerField, onStatisticalDataReady }) => {
  const { register, watch, setValue } = useFormContext();
  const watchedValues = watch();
  const { isInternalUser } = useAuthGuard();
  const [isCollapsibleOpen, setIsCollapsibleOpen] = React.useState(false);
  const [originalOperatingHours, setOriginalOperatingHours] =
    React.useState<any>(null);
  const { pathname } = useLocation();
  const { toast } = useToast();

  // Days of the week mapping
  const daysOfWeek = [
    { key: "sunday", label: "Domingo" },
    { key: "monday", label: "Segunda-feira" },
    { key: "tuesday", label: "Terça-feira" },
    { key: "wednesday", label: "Quarta-feira" },
    { key: "thursday", label: "Quinta-feira" },
    { key: "friday", label: "Sexta-feira" },
    { key: "saturday", label: "Sábado" },
  ];

  const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
  const SATURDAY_ONLY = ["saturday"];

  useEffect(() => {
    // Set timezone from form data or default
    if (!watchedValues.timezone) {
      setValue("timezone", "America/Sao_Paulo");
    }

    // Store original operating hours from backend for restoration
    if (watchedValues.operatingHours && !originalOperatingHours) {
      setOriginalOperatingHours(
        JSON.parse(JSON.stringify(watchedValues.operatingHours)),
      );
    }

    // Initialize operating hours pattern detection
    if (watchedValues.operatingHours && !watchedValues.daysOfOperation) {
      const operatingHours = watchedValues.operatingHours;

      // Count which days have operating hours
      const weekdaysWithHours = WEEKDAYS.filter(
        (day) => operatingHours[day] && operatingHours[day].length > 0,
      );
      const hasSaturday =
        operatingHours.saturday && operatingHours.saturday.length > 0;
      const hasSunday =
        operatingHours.sunday && operatingHours.sunday.length > 0;

      console.log("Detecting pattern:", {
        weekdaysWithHours,
        hasSaturday,
        hasSunday,
        totalWeekdays: weekdaysWithHours.length,
      });

      // More precise pattern detection
      if (weekdaysWithHours.length === 5 && hasSaturday && !hasSunday) {
        // All weekdays + Saturday
        setValue("daysOfOperation", "monday-saturday");
      } else if (weekdaysWithHours.length === 5 && !hasSaturday && !hasSunday) {
        // All weekdays only
        setValue("daysOfOperation", "monday-friday");
      } else {
        // Any other combination is custom
        setValue("daysOfOperation", "custom");
      }
    } else if (!watchedValues.daysOfOperation) {
      setValue("daysOfOperation", "monday-friday");
    }
  }, [
    watchedValues.timezone,
    watchedValues.operatingHours,
    watchedValues.daysOfOperation,
    originalOperatingHours,
    setValue,
  ]);

  const { spaces, spacesPO, handleSearchSpaces, handleSpaceSelect } =
    useSpaceSelection({});

  // Debug log to see the data
  useEffect(() => {
    if (watchedValues.operatingHours) {
      console.log(
        "Form operating hours:",
        JSON.stringify(watchedValues.operatingHours, null, 2),
      );
      console.log("Days of operation:", watchedValues.daysOfOperation);
      console.log("Timezone:", watchedValues.timezone);
      console.log("Original operating hours (backup):", originalOperatingHours);
    }
  }, [
    watchedValues.operatingHours,
    watchedValues.daysOfOperation,
    watchedValues.timezone,
    originalOperatingHours,
  ]);

  // Handle days of operation selection
  const handleDaysOfOperationChange = (value: string) => {
    setValue("daysOfOperation", value);

    const defaultHours = { open: "08:00", close: "18:00" };

    switch (value) {
      case "monday-friday":
        setValue("operatingHours", {
          sunday: [],
          monday: [defaultHours],
          tuesday: [defaultHours],
          wednesday: [defaultHours],
          thursday: [defaultHours],
          friday: [defaultHours],
          saturday: [],
        });
        break;
      case "monday-saturday":
        setValue("operatingHours", {
          sunday: [],
          monday: [defaultHours],
          tuesday: [defaultHours],
          wednesday: [defaultHours],
          thursday: [defaultHours],
          friday: [defaultHours],
          saturday: [{ open: "08:00", close: "14:00" }],
        });
        break;
      case "custom":
        // Restore original operating hours from backend when switching to custom
        if (originalOperatingHours) {
          console.log(
            "Restoring original operating hours:",
            originalOperatingHours,
          );
          setValue(
            "operatingHours",
            JSON.parse(JSON.stringify(originalOperatingHours)),
          );
        }
        break;
    }
  };

  // Handle operating hours changes
  const handleDayToggle = (dayKey: string, enabled: boolean) => {
    const currentOperatingHours = watchedValues.operatingHours || {};
    const currentDayHours = currentOperatingHours[dayKey] || [];

    if (enabled && currentDayHours.length === 0) {
      // Add default time slot when enabling
      setValue("operatingHours", {
        ...currentOperatingHours,
        [dayKey]: [{ open: "08:00", close: "18:00" }],
      });
    } else if (!enabled) {
      // Remove all time slots when disabling
      setValue("operatingHours", {
        ...currentOperatingHours,
        [dayKey]: [],
      });
    }
  };

  const getCurrentTimeValue = (
    timeType: "open" | "close",
    daysToCheck: string[],
    defaultValue: string,
  ) => {
    const operatingHours = watchedValues.operatingHours || {};
    for (const day of daysToCheck) {
      if (operatingHours[day]?.[0]?.[timeType]) {
        return operatingHours[day][0][timeType];
      }
    }
    return defaultValue;
  };

  const TimeSelect = ({
    timeType,
    daysToCheck,
    defaultValue,
    onValueChange,
    disabled = false,
  }: {
    timeType: "open" | "close";
    daysToCheck: string[];
    defaultValue: string;
    onValueChange: (value: string) => void;
    disabled?: boolean;
  }) => (
    <Select
      value={getCurrentTimeValue(timeType, daysToCheck, defaultValue)}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-24">
        <SelectValue placeholder={defaultValue} />
      </SelectTrigger>
      <SelectContent>
        {Array.from({ length: 24 }, (_, i) => {
          const hour = i.toString().padStart(2, "0");
          return (
            <SelectItem key={hour} value={`${hour}:00`}>
              {hour}:00
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  const handleTimeChange = (
    dayKey: string,
    timeType: "open" | "close",
    value: string,
  ) => {
    const currentOperatingHours = watchedValues.operatingHours || {};
    const daysOfOperation = watchedValues.daysOfOperation || "monday-friday";

    let daysToUpdate: string[] = [];

    if (daysOfOperation === "monday-friday") {
      daysToUpdate = [...WEEKDAYS];
    } else if (daysOfOperation === "monday-saturday") {
      if (dayKey === "saturday") {
        daysToUpdate = [...SATURDAY_ONLY];
      } else {
        daysToUpdate = [...WEEKDAYS];
      }
    } else if (daysOfOperation === "custom") {
      daysToUpdate = [dayKey];
    }

    const updatedOperatingHours = { ...currentOperatingHours };

    daysToUpdate.forEach((day) => {
      const currentDayHours = updatedOperatingHours[day] || [];

      const defaultClose = day === "saturday" ? "14:00" : "18:00";
      const updatedDayHours =
        currentDayHours.length > 0
          ? [...currentDayHours]
          : [{ open: "08:00", close: defaultClose }];

      updatedDayHours[0] = {
        ...updatedDayHours[0],
        [timeType]: value,
      };

      updatedOperatingHours[day] = updatedDayHours;
    });

    if (daysOfOperation !== "custom") {
      const allDays = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      allDays.forEach((day) => {
        if (
          daysOfOperation === "monday-friday" &&
          !WEEKDAYS.includes(day as any)
        ) {
          updatedOperatingHours[day] = [];
        } else if (daysOfOperation === "monday-saturday" && day === "sunday") {
          updatedOperatingHours[day] = [];
        }
      });
    }

    setValue("operatingHours", updatedOperatingHours, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const formatCurrency = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");

    if (!cleanedValue) return "";

    const numberValue = parseFloat(cleanedValue) / 100;
    return numberValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatInitialMonthlyPrice = (value: string | number) => {
    if (!value) return "";

    if (typeof value === "string" && /^\d{1,3}(\.\d{3})*,\d{2}$/.test(value)) {
      return value;
    }

    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "";

    return numValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const validateName = (name: string): string | null => {
    if (!name || name.trim() === "") {
      return "O campo Nome é obrigatório";
    }
    if (name.length > 50) {
      return "O nome da sala deve ter no máximo 50 caracteres";
    }
    return null;
  };

  const validatePrice = (price: string): string | null => {
    if (!price || price.trim() === "") {
      return "O campo Preço mensal é obrigatório";
    }
    const cleanedValue = price.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(cleanedValue);
    if (isNaN(num) || num < 500) {
      return "O preço mensal deve ser igual ou maior que R$ 500";
    }
    return null;
  };

  const [nameError, setNameError] = React.useState<string | null>(null);
  const [priceError, setPriceError] = React.useState<string | null>(null);

  // Get neighborhood from form state (comes from loader data via initialData)
  const neighborhood = watchedValues.neighborhood || null;
  const [neighborhoodFetched, setNeighborhoodFetched] = React.useState<boolean>(
    () => Boolean(watchedValues.neighborhood),
  );

  // Statistical analysis hook
  const {
    data: statisticalData,
    isLoading: isLoadingAnalysis,
    hasRequiredData,
    hasNeighborhood,
    localSizeCapacityRatio,
  } = useStatisticalAnalysis({
    neighborhood,
    monthlyPrice: watchedValues.monthlyPrice,
    capacity: watchedValues.capacity,
    sizeM2: watchedValues.size,
    enabled: Boolean(watchedValues.capacity && watchedValues.size),
  });

  // Notify parent about statistical data changes
  useEffect(() => {
    if (statisticalData && onStatisticalDataReady) {
      const hasExtremeOutliers = {
        isExtremePrice:
          statisticalData.analyses?.monthlyPrice?.outlierDetection?.status !==
          "within_range",
        isExtremeRatio:
          statisticalData.spaceRatioValidation?.status === "below_minimum",
        hasAnyExtreme:
          statisticalData.analyses?.monthlyPrice?.outlierDetection?.status !==
            "within_range" ||
          statisticalData.spaceRatioValidation?.status === "below_minimum",
      };

      onStatisticalDataReady({
        statisticalData,
        hasExtremeOutliers,
      });
    }
  }, [statisticalData, onStatisticalDataReady]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue("name", value);
    setNameError(validateName(value));
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatCurrency(inputValue);
    setValue("monthlyPrice", formattedValue);
    setPriceError(validatePrice(formattedValue));
  };

  const handleSpaceSelectAndExpand = async (spaceId: string) => {
    setValue("externalSpaceId", spaceId);
    const selectedSpace = spaces.find((space) => space.value === spaceId);
    setValue("spaceName", selectedSpace?.label || "");
    setValue("neighborhood", null);
    setNeighborhoodFetched(false);

    // Fetch space details including neighborhood
    try {
      const space = await spaceService.getSpaceById({ spaceId });
      setValue("neighborhood", space.neighborhood);
    } catch (error) {
      console.error("Failed to fetch space neighborhood:", error);
    } finally {
      setNeighborhoodFetched(true);
    }

    await handleSpaceSelect({ spaceId });
  };

  // Use the stored space name or fallback to finding it in the array
  const selectedSpaceLabel = useMemo(() => {
    if (!watchedValues.externalSpaceId) return "";
    if (watchedValues.spaceName) return watchedValues.spaceName;
    const selectedSpace = spaces.find(
      (space) => space.value === watchedValues.externalSpaceId,
    );
    return selectedSpace?.label || "";
  }, [watchedValues.externalSpaceId, watchedValues.spaceName, spaces]);

  // Store rooms in form when fetched
  useEffect(() => {
    if (spacesPO.length > 0) {
      setValue("spaceRooms", spacesPO);
    }
  }, [spacesPO, setValue]);

  // Auto-fetch space name and rooms when externalSpaceId exists but spaceName doesn't (from API)
  useEffect(() => {
    const fetchSpaceData = async () => {
      if (
        watchedValues.externalSpaceId &&
        !watchedValues.spaceName &&
        selectedSpaceLabel === ""
      ) {
        try {
          const spaceData = await spaceService.getSpaceById({
            spaceId: watchedValues.externalSpaceId,
          });
          setValue("spaceName", spaceData.name);
        } catch (error) {
          console.error("Failed to fetch space name:", error);
        }
        handleSpaceSelect({ spaceId: watchedValues.externalSpaceId });
      }
    };

    fetchSpaceData();
  }, [
    watchedValues.externalSpaceId,
    watchedValues.spaceName,
    selectedSpaceLabel,
    handleSpaceSelect,
    setValue,
  ]);

  useEffect(() => {
    const fetchNeighborhood = async () => {
      if (
        watchedValues.externalSpaceId &&
        !watchedValues.neighborhood &&
        !neighborhoodFetched
      ) {
        try {
          const space = await spaceService.getSpaceById({
            spaceId: watchedValues.externalSpaceId,
          });
          setValue("neighborhood", space.neighborhood);
        } catch (error) {
          console.error("Failed to fetch space neighborhood:", error);
        } finally {
          setNeighborhoodFetched(true);
        }
      }
    };

    fetchNeighborhood();
  }, [
    watchedValues.externalSpaceId,
    watchedValues.neighborhood,
    neighborhoodFetched,
    setValue,
  ]);

  const handleCloneRoom = (po: any) => {
    setValue("name", po.label);
    setValue("size", po.size?.toString());
    setValue("capacity", po.capacity.toString());
    // Convert numeric status to string status using the mapping
    const stringStatus =
      NUMERIC_TO_STRING_STATUS_MAP[
        po.status as keyof typeof NUMERIC_TO_STRING_STATUS_MAP
      ] || PrivateOfficeStatus.AVAILABLE;
    setValue("status", stringStatus);
    setValue(
      "monthlyPrice",
      po.monthlyPrice.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );
    setValue("notes", po.note || "");

    // Close the collapsible section
    setIsCollapsibleOpen(false);

    // Focus on the first input field after a short delay
    setTimeout(() => {
      const nameInput = document.getElementById("name");
      if (nameInput) {
        nameInput.focus();
        nameInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 300);

    toast({
      title: "Clonagem concluída com sucesso!",
      description: `A sala ${po.label} (ID ${po.search_id}) foi copiada e já está pronta para edição.`,
    });
  };

  const handleVisualizeRoom = (po: any) => {
    const roomUrl = generatePrivateRoomUrl(po.id.toString(), "80");

    sendTrackDataServer({
      url: pathname,
      eventName: "VIEW_PAGE_CLICKED",
      productType: "PO",
      data: {
        room_id: po.id,
      },
    });

    window.open(roomUrl, "_blank", "noopener,noreferrer");
  };

  // Check if space is already selected (from API data like parentId)
  const hasPreselectedSpace = !!(
    watchedValues.externalSpaceId && watchedValues.externalSpaceId.trim() !== ""
  );

  // Check if space is already selected (from API data like parentId)
  const fieldsDisabled = false; // For edit, fields are always enabled

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">
          Vincular sala a um espaço
        </h3>
        <div className="space-y-4">
          <div
            ref={
              errors.externalSpaceId && registerField
                ? (el) => registerField("externalSpaceId", el)
                : undefined
            }
          >
            <Label htmlFor="externalSpaceId">Espaço</Label>
            {watchedValues.externalSpaceId ? (
              <div className="flex items-center justify-between w-full p-3 border border-input bg-gray-100 rounded-md">
                <span className="text-sm text-gray-700">
                  {selectedSpaceLabel ||
                    `Espaço ID: ${watchedValues.externalSpaceId}`}
                </span>
                <span className="text-xs bg-slate-300 text-[#64748B] px-2 py-1 rounded italic">
                  Espaço vinculado
                </span>
              </div>
            ) : (
              <ComboboxDemo
                key="space-selector"
                defaultValue=""
                emptyPlaceholder="Espaço não encontrado"
                searchPlaceholder="Busque um espaço"
                selectPlaceholder="Busque ou selecione um espaço"
                widthContent="w-full"
                onChange={debounce(handleSearchSpaces, 500)}
                items={spaces}
                onSelect={handleSpaceSelectAndExpand}
                shouldFilter={false}
                disabled={hasPreselectedSpace}
              />
            )}
            {errors.externalSpaceId && (
              <p className="text-sm text-destructive mt-1">
                {errors.externalSpaceId[0]}
              </p>
            )}
          </div>

          {watchedValues.spaceRooms && watchedValues.spaceRooms.length > 0 && (
            <div>
              <Button
                variant="ghost"
                onClick={() => setIsCollapsibleOpen(!isCollapsibleOpen)}
                className="flex items-center justify-between w-full p-0 h-auto font-normal text-left hover:bg-transparent"
              >
                <span className="text-base">
                  Salas já cadastradas nesse espaço
                </span>
                {isCollapsibleOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>

              {isCollapsibleOpen && (
                <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
                  {watchedValues.spaceRooms.map((po: any) => (
                    <div
                      key={po.id}
                      className="flex items-center justify-between w-full p-2 rounded-lg"
                      style={{
                        backgroundColor: "#FCFBF9",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="flex-1 text-gray-700 text-sm">
                        <span>
                          ID {po.search_id} | Capacidade {po.capacity} pessoas |
                          Preço R${" "}
                          {po.monthlyPrice.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      <div className="flex gap-4 ml-4">
                        <Button
                          variant="outline"
                          onClick={() => handleVisualizeRoom(po)}
                          className="text-gray-600 hover:text-gray-900 px-4 py-2 font-normal"
                        >
                          Visualizar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleCloneRoom(po)}
                          className="text-gray-600 hover:text-gray-900 px-4 py-2 font-normal"
                        >
                          Clonar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Informações da sala</h3>
        <div className="space-y-4">
          <div
            ref={
              errors.name && registerField
                ? (el) => registerField("name", el)
                : undefined
            }
          >
            <Label htmlFor="name">Nome*</Label>
            <Input
              id="name"
              placeholder="Ex: Sala Rio 01, Privativa Alpha"
              value={watchedValues.name || ""}
              onChange={handleNameChange}
              disabled={fieldsDisabled}
            />
            {(nameError || errors.name) && (
              <p className="text-sm text-destructive mt-1">
                {nameError || errors.name?.[0]}
              </p>
            )}
          </div>

          <div
            ref={
              errors.description && registerField
                ? (el) => registerField("description", el)
                : undefined
            }
          >
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              rows={4}
              value={watchedValues.description || ""}
              onChange={(e) => setValue("description", e.target.value)}
              className="mt-2"
              disabled={fieldsDisabled}
            />
            <div className="flex justify-between items-center mt-1">
              <div>
                {errors.description && (
                  <p className="text-sm text-destructive">
                    {errors.description[0]}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {(watchedValues.description || "").length} caracteres
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              ref={
                errors.size && registerField
                  ? (el) => registerField("size", el)
                  : undefined
              }
            >
              <Label htmlFor="size">Tamanho em m²*</Label>
              <Input
                id="size"
                type="number"
                min="0"
                step="0.1"
                placeholder="35"
                {...register("size")}
                disabled={fieldsDisabled}
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") {
                    e.preventDefault();
                  }
                }}
              />
              {errors.size && (
                <p className="text-sm text-destructive mt-1">
                  {errors.size[0]}
                </p>
              )}
            </div>

            <div
              ref={
                errors.capacity && registerField
                  ? (el) => registerField("capacity", el)
                  : undefined
              }
            >
              <Label htmlFor="capacity">Capacidade*</Label>
              <Input
                id="capacity"
                type="number"
                min="0"
                step="1"
                placeholder="8"
                {...register("capacity")}
                disabled={fieldsDisabled}
                onKeyDown={(e) => {
                  if (
                    e.key === "-" ||
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "."
                  ) {
                    e.preventDefault();
                  }
                }}
              />
              {errors.capacity && (
                <p className="text-sm text-destructive mt-1">
                  {errors.capacity[0]}
                </p>
              )}
            </div>
          </div>

          {watchedValues.externalSpaceId &&
          neighborhoodFetched &&
          !hasNeighborhood ? (
            <NeighborhoodMissingAlert
              spaceId={watchedValues.externalSpaceId}
              spaceName={selectedSpaceLabel}
              screen="private-office-edit"
            />
          ) : (
            <SizeCapacityAlert
              spaceRatioValidation={statisticalData?.spaceRatioValidation}
              localSizeCapacityRatio={localSizeCapacityRatio}
              loading={isLoadingAnalysis}
            />
          )}

          <div
            ref={
              errors.status && registerField
                ? (el) => registerField("status", el)
                : undefined
            }
          >
            <Label htmlFor="status">Status*</Label>
            <Select
              value={watchedValues.status || ""}
              onValueChange={(value) => {
                setValue("status", value);
                if (value !== PrivateOfficeStatus.UNAVAILABLE) {
                  setValue("lifecycleState", undefined);
                  setValue("availabilityDate", undefined);
                }
              }}
              disabled={fieldsDisabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PrivateOfficeStatus.AVAILABLE}>
                  Disponível
                </SelectItem>
                <SelectItem value={PrivateOfficeStatus.UNAVAILABLE}>
                  Indisponível
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.status && (
              <p className="text-sm text-destructive mt-1">
                {errors.status[0]}
              </p>
            )}
          </div>

          {watchedValues.status === PrivateOfficeStatus.UNAVAILABLE && (
            <>
              <div
                ref={
                  errors.lifecycleState && registerField
                    ? (el) => registerField("lifecycleState", el)
                    : undefined
                }
              >
                <Label htmlFor="lifecycleState">
                  Motivo de indisponibilidade*
                </Label>
                <Select
                  value={watchedValues.lifecycleState || ""}
                  onValueChange={(value) => {
                    const state = value as PrivateOfficeLifecycleState;
                    setValue("lifecycleState", state);
                    if (!isTransientLifecycleState(state)) {
                      setValue("availabilityDate", undefined);
                    }
                  }}
                  disabled={fieldsDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {(isInternalUser
                      ? MANUAL_LIFECYCLE_STATE_OPTIONS_INTERNAL_USER
                      : MANUAL_LIFECYCLE_STATE_OPTIONS
                    ).map((v) => (
                      <SelectItem key={v} value={v}>
                        {LIFECYCLE_STATE_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.lifecycleState && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.lifecycleState?.[0]}
                  </p>
                )}
              </div>

              {watchedValues.lifecycleState &&
                isTransientLifecycleState(
                  watchedValues.lifecycleState as PrivateOfficeLifecycleState,
                ) && (
                  <div
                    ref={
                      errors.availabilityDate && registerField
                        ? (el) => registerField("availabilityDate", el)
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="availabilityDate">
                        Previsão de disponibilidade*
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-default" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-96 bg-black text-white">
                            Esta data é apenas uma previsão informativa. Nenhuma
                            ação automática é executada ao atingi-la — a sala
                            não volta a ficar disponível automaticamente.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="availabilityDate"
                      type="date"
                      min={todayIso}
                      value={watchedValues.availabilityDate || ""}
                      onChange={(e) => {
                        if (
                          e.target.value >= todayIso ||
                          e.target.value === ""
                        ) {
                          setValue("availabilityDate", e.target.value);
                        }
                      }}
                      disabled={fieldsDisabled}
                      className="mt-1"
                    />
                    {errors.availabilityDate && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.availabilityDate?.[0]}
                      </p>
                    )}
                  </div>
                )}
            </>
          )}

          <div>
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              rows={4}
              {...register("notes")}
              className="mt-2"
              disabled={fieldsDisabled}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Apenas visível para o time interno
            </p>
            {errors.notes && (
              <p className="text-sm text-destructive mt-1">{errors.notes[0]}</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Horário de funcionamento</h3>

        <div className="space-y-6">
          <div>
            <h4 className="text-base font-medium mb-4">
              Dias de funcionamento
            </h4>
            <Select
              value={watchedValues.daysOfOperation || "monday-friday"}
              onValueChange={handleDaysOfOperationChange}
              disabled={fieldsDisabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monday-friday">Segunda à sexta</SelectItem>
                <SelectItem value="monday-saturday">
                  Segunda à sábado
                </SelectItem>
                <SelectItem value="custom">Personalizado por dia</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-2">
              Escolha em quais dias da semana a sala estará disponível
            </p>
          </div>

          {watchedValues.daysOfOperation === "monday-friday" && (
            <div>
              <h4 className="text-base font-medium mb-4">
                Horário de Funcionamento (Segunda a sexta)
              </h4>
              <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-md">
                <TimeSelect
                  timeType="open"
                  daysToCheck={WEEKDAYS}
                  defaultValue="08:00"
                  onValueChange={(value) => {
                    WEEKDAYS.forEach((day) => {
                      handleTimeChange(day, "open", value);
                    });
                  }}
                  disabled={fieldsDisabled}
                />
                <span className="text-sm text-muted-foreground">às</span>
                <TimeSelect
                  timeType="close"
                  daysToCheck={WEEKDAYS}
                  defaultValue="18:00"
                  onValueChange={(value) => {
                    WEEKDAYS.forEach((day) => {
                      handleTimeChange(day, "close", value);
                    });
                  }}
                  disabled={fieldsDisabled}
                />
              </div>
            </div>
          )}

          {watchedValues.daysOfOperation === "monday-saturday" && (
            <div className="space-y-4">
              <div>
                <h4 className="text-base font-medium mb-4">
                  Horário de Funcionamento (Segunda a sexta)
                </h4>
                <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-md">
                  <TimeSelect
                    timeType="open"
                    daysToCheck={WEEKDAYS}
                    defaultValue="08:00"
                    onValueChange={(value) => {
                      WEEKDAYS.forEach((day) => {
                        handleTimeChange(day, "open", value);
                      });
                    }}
                    disabled={fieldsDisabled}
                  />
                  <span className="text-sm text-muted-foreground">às</span>
                  <TimeSelect
                    timeType="close"
                    daysToCheck={WEEKDAYS}
                    defaultValue="18:00"
                    onValueChange={(value) => {
                      WEEKDAYS.forEach((day) => {
                        handleTimeChange(day, "close", value);
                      });
                    }}
                    disabled={fieldsDisabled}
                  />
                </div>
              </div>
              <div>
                <h4 className="text-base font-medium mb-4">
                  Horário de Funcionamento (Sábado)
                </h4>
                <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-md">
                  <TimeSelect
                    timeType="open"
                    daysToCheck={SATURDAY_ONLY}
                    defaultValue="08:00"
                    onValueChange={(value) =>
                      handleTimeChange("saturday", "open", value)
                    }
                    disabled={fieldsDisabled}
                  />
                  <span className="text-sm text-muted-foreground">às</span>
                  <TimeSelect
                    timeType="close"
                    daysToCheck={SATURDAY_ONLY}
                    defaultValue="14:00"
                    onValueChange={(value) =>
                      handleTimeChange("saturday", "close", value)
                    }
                    disabled={fieldsDisabled}
                  />
                </div>
              </div>
            </div>
          )}

          {watchedValues.daysOfOperation === "custom" && (
            <div>
              <h4 className="text-base font-medium mb-4">
                Horários personalizados por dia
              </h4>
              <div className="space-y-4">
                {daysOfWeek.map((day) => {
                  const dayHours =
                    watchedValues.operatingHours?.[day.key] || [];
                  const isEnabled = dayHours.length > 0;
                  const firstSlot = dayHours[0];

                  return (
                    <div key={day.key} className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 min-w-[140px]">
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked: boolean) =>
                            handleDayToggle(day.key, checked)
                          }
                          disabled={fieldsDisabled}
                        />
                        <Label className="text-sm">{day.label}</Label>
                      </div>

                      {isEnabled && firstSlot ? (
                        <div className="flex items-center space-x-2">
                          <Select
                            value={firstSlot.open}
                            onValueChange={(value) =>
                              handleTimeChange(day.key, "open", value)
                            }
                            disabled={fieldsDisabled}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, "0");
                                return (
                                  <SelectItem key={hour} value={`${hour}:00`}>
                                    {hour}:00
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <span className="text-sm text-muted-foreground">
                            às
                          </span>
                          <Select
                            value={firstSlot.close}
                            onValueChange={(value) =>
                              handleTimeChange(day.key, "close", value)
                            }
                            disabled={fieldsDisabled}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, "0");
                                return (
                                  <SelectItem key={hour} value={`${hour}:00`}>
                                    {hour}:00
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : !isEnabled ? (
                        <span className="text-sm text-muted-foreground">
                          Fechado
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Valor</h3>
        <div className="space-y-4">
          <div
            ref={
              errors.monthlyPrice && registerField
                ? (el) => registerField("monthlyPrice", el)
                : undefined
            }
          >
            <Label htmlFor="monthlyPrice">Preço mensal*</Label>
            <Input
              id="monthlyPrice"
              type="text"
              placeholder="Ex.: 1.800,00"
              value={
                watchedValues.monthlyPrice
                  ? formatInitialMonthlyPrice(watchedValues.monthlyPrice)
                  : ""
              }
              onChange={handlePriceChange}
              disabled={fieldsDisabled}
              onKeyDown={(e) => {
                if (e.key === "-") {
                  e.preventDefault();
                }
              }}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Mínimo de R$ 500
            </p>
            {(priceError || errors.monthlyPrice) && (
              <p className="text-sm text-destructive mt-1">
                {priceError || errors.monthlyPrice?.[0]}
              </p>
            )}

            {watchedValues.externalSpaceId &&
            neighborhoodFetched &&
            !hasNeighborhood ? null : (
              <PriceAnalysisAlert
                analysis={statisticalData?.analyses?.monthlyPrice}
                loading={isLoadingAnalysis}
                hasRequiredData={hasRequiredData}
                neighborhood={neighborhood}
                sizeM2={watchedValues.size}
                capacity={watchedValues.capacity}
                monthlyPrice={watchedValues.monthlyPrice}
                sampleSize={statisticalData?.sampleSize}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
